/**
 * React Query hook for a single challenge (detail screen).
 * Uses placeholderData from the list cache (active + history) so opening from Arena feels instant;
 * then refetches GET /challenges/:id in the background for full details.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { fetchChallenge } from '../helpers/challengesApi';
import { USER_CONFIG } from '../../../constants';
import type { ChallengeWithStats } from '../../../types/challenges';

/**
 * Read a challenge from the list cache (active or history) for use as placeholderData.
 * Ensures the detail screen can show list data immediately while the full fetch runs.
 */
function getChallengeFromListCache(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  challengeId: string
): ChallengeWithStats | undefined {
  const active = queryClient.getQueryData<ChallengeWithStats[]>(['challenges', 'active', userId]);
  const found = active?.find((c) => c.id === challengeId);
  if (found) return found;

  const historyData = queryClient.getQueryData<{ pages: { data: ChallengeWithStats[] }[] }>([
    'challenges',
    'history',
    userId,
  ]);
  const fromHistory = historyData?.pages?.flatMap((p) => p.data).find((c) => c.id === challengeId);
  return fromHistory;
}

export function useChallengeDetailQuery(challengeId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { userId, isLoaded, isSignedIn } = useSupabaseAuth();
  const currentUserId = userId ?? USER_CONFIG.defaultUserId;
  const enabled = !!challengeId && isSignedIn === true && isLoaded === true;

  return useQuery({
    queryKey: ['challenges', 'detail', challengeId ?? ''],
    queryFn: () => fetchChallenge(challengeId!, apiClient),
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: () =>
      challengeId && currentUserId
        ? getChallengeFromListCache(queryClient, currentUserId, challengeId) ?? undefined
        : undefined,
  });
}
